/**
 * Client-side Meta (Facebook) Pixel wrapper.
 *
 * Design goals
 * ------------
 *  1. **Consent-gated** — the pixel script is NOT loaded until the visitor
 *     accepts non-essential cookies via the CookieBanner. This keeps us
 *     compliant with GDPR/CCPA out of the box.
 *  2. **Idempotent init** — safe to import/init multiple times; the base
 *     script is loaded once, subsequent calls are no-ops.
 *  3. **Silent no-op** when `REACT_APP_META_PIXEL_ID` isn't set (dev/tests).
 *  4. **Route-aware PageView** — automatic pageview on every SPA route
 *     change is done via <MetaPixelRouteTracker /> mounted once in AppRouter.
 *  5. **Standard events only** — `trackEvent(name, params)` fires
 *     `fbq('track', name, params)` for cleanly-attributable ad campaigns.
 */

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const CONSENT_KEY = 'propveiw_cookie_consent_v1';

let initialized = false;

/** Was the pixel actually booted? */
export const isPixelReady = () => initialized && !!window.fbq;

/** Read the current cookie-consent choice. Returns 'all' | 'essential_only' | 'dismissed' | null. */
export const getConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.choice || null;
  } catch {
    return null;
  }
};

const hasConsent = () => getConsent() === 'all';

/**
 * Load the Facebook Pixel base script and call `init`. Safe to call more
 * than once — subsequent calls do nothing.
 */
export const initMetaPixel = () => {
  if (initialized) return;
  if (!PIXEL_ID) return;               // No pixel configured — silent no-op
  if (typeof window === 'undefined') return;
  if (!hasConsent()) return;           // Wait for user opt-in

  // ---- Standard Meta bootstrap ----
  /* eslint-disable */
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
  initialized = true;

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.info(`[MetaPixel] initialized (id=${PIXEL_ID})`);
  }
};

/**
 * Track a Meta standard event. If the pixel isn't ready yet (no consent
 * given, or PIXEL_ID missing) this is a no-op.
 *
 * Common events:
 *   trackEvent('PageView')
 *   trackEvent('ViewContent', { content_name: 'Pricing' })
 *   trackEvent('Lead', { content_name: 'Waitlist - Beta Banner' })
 *   trackEvent('CompleteRegistration')
 *   trackEvent('InitiateCheckout', { value: 79, currency: 'USD' })
 *   trackEvent('Subscribe', { value: 79, currency: 'USD' })
 */
export const trackEvent = (name, params = {}) => {
  if (!isPixelReady()) return;
  try {
    window.fbq('track', name, params);
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.info(`[MetaPixel] track ${name}`, params);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('MetaPixel track error:', err);
  }
};

/**
 * Called by CookieBanner after the user accepts all cookies. Boots the
 * pixel on the fly so we don't need a full page reload.
 */
export const onConsentGranted = () => {
  initMetaPixel();
};
